import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from '@mui/icons-material/Save';
import { SelectChangeEvent } from '@mui/material/Select';

// Mock data for plans and permissions
const mockPlans = ['Basic', 'Standard', 'Premium', 'Enterprise'];

interface Permission {
  id: number;
  name: string;
  description: string;
  category: string;
  planAccess: Record<string, boolean>;
}

const mockPermissions: Permission[] = [
  {
    id: 1,
    name: 'Dashboard View',
    description: 'Access to view the main dashboard',
    category: 'Dashboard',
    planAccess: {
      Basic: true,
      Standard: true,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 2,
    name: 'User Management',
    description: 'Create, edit, and delete users',
    category: 'Users',
    planAccess: {
      Basic: false,
      Standard: true,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 3,
    name: 'Financial Reports',
    description: 'Access to financial reporting tools',
    category: 'Reports',
    planAccess: {
      Basic: false,
      Standard: false,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 4,
    name: 'API Access',
    description: 'Access to the API endpoints',
    category: 'Integration',
    planAccess: {
      Basic: false,
      Standard: true,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 5,
    name: 'Custom Branding',
    description: 'Ability to customize branding elements',
    category: 'Customization',
    planAccess: {
      Basic: false,
      Standard: false,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 6,
    name: 'Audit Logs',
    description: 'View detailed audit logs of system actions',
    category: 'Security',
    planAccess: {
      Basic: false,
      Standard: false,
      Premium: false,
      Enterprise: true,
    },
  },
  {
    id: 7,
    name: 'Email Notifications',
    description: 'Configure and send email notifications',
    category: 'Communication',
    planAccess: {
      Basic: true,
      Standard: true,
      Premium: true,
      Enterprise: true,
    },
  },
  {
    id: 8,
    name: 'Advanced Analytics',
    description: 'Access to advanced analytics and reporting',
    category: 'Reports',
    planAccess: {
      Basic: false,
      Standard: false,
      Premium: true,
      Enterprise: true,
    },
  },
];

// Unique categories for filtering
const categories = Array.from(new Set(mockPermissions.map(p => p.category)));

interface PermissionDialogState {
  open: boolean;
  permission: Permission | null;
  isNew: boolean;
}

const Permissions: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>(mockPermissions);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [dialogState, setDialogState] = useState<PermissionDialogState>({
    open: false,
    permission: null,
    isNew: false,
  });
  const [hasChanges, setHasChanges] = useState(false);

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setSelectedCategory(event.target.value);
  };

  const filteredPermissions = selectedCategory === 'All'
    ? permissions
    : permissions.filter(p => p.category === selectedCategory);

  const handleTogglePermission = (permissionId: number, plan: string) => {
    const updatedPermissions = permissions.map(permission => {
      if (permission.id === permissionId) {
        return {
          ...permission,
          planAccess: {
            ...permission.planAccess,
            [plan]: !permission.planAccess[plan],
          },
        };
      }
      return permission;
    });
    
    setPermissions(updatedPermissions);
    setHasChanges(true);
  };

  const handleAddPermission = () => {
    const newPermission: Permission = {
      id: permissions.length + 1,
      name: '',
      description: '',
      category: categories[0],
      planAccess: {
        Basic: false,
        Standard: false,
        Premium: false,
        Enterprise: false,
      },
    };
    
    setDialogState({
      open: true,
      permission: newPermission,
      isNew: true,
    });
  };

  const handleEditPermission = (permission: Permission) => {
    setDialogState({
      open: true,
      permission: { ...permission },
      isNew: false,
    });
  };

  const handleCloseDialog = () => {
    setDialogState({
      open: false,
      permission: null,
      isNew: false,
    });
  };

  const handleSavePermission = () => {
    if (!dialogState.permission) return;

    if (dialogState.isNew) {
      setPermissions([...permissions, dialogState.permission]);
    } else {
      setPermissions(
        permissions.map((p) => (p.id === dialogState.permission?.id ? dialogState.permission : p))
      );
    }

    setHasChanges(true);
    handleCloseDialog();
  };

  const handleSaveChanges = () => {
    // In a real application, this would save changes to your backend
    setHasChanges(false);
    // Show success message or feedback
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Permissions Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {hasChanges && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
            >
              Save Changes
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddPermission}
          >
            Add Permission
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1">Filter by Category:</Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <Select
              value={selectedCategory}
              onChange={handleCategoryChange}
              displayEmpty
              size="small"
            >
              <MenuItem value="All">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Permission</TableCell>
              <TableCell>Category</TableCell>
              {mockPlans.map((plan) => (
                <TableCell key={plan} align="center">{plan}</TableCell>
              ))}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPermissions.map((permission) => (
              <TableRow key={permission.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {permission.name}
                    <Tooltip title={permission.description}>
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={permission.category} size="small" />
                </TableCell>
                {mockPlans.map((plan) => (
                  <TableCell key={plan} align="center">
                    <Switch
                      checked={permission.planAccess[plan]}
                      onChange={() => handleTogglePermission(permission.id, plan)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleEditPermission(permission)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Permission Dialog */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogState.isNew ? 'Add New Permission' : 'Edit Permission'}
        </DialogTitle>
        <DialogContent dividers>
          {dialogState.permission && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Permission Name"
                value={dialogState.permission.name}
                onChange={(e) => 
                  setDialogState({
                    ...dialogState,
                    permission: { ...dialogState.permission!, name: e.target.value },
                  })
                }
              />
              <TextField
                fullWidth
                label="Description"
                value={dialogState.permission.description}
                onChange={(e) => 
                  setDialogState({
                    ...dialogState,
                    permission: { ...dialogState.permission!, description: e.target.value },
                  })
                }
                multiline
                rows={2}
              />
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={dialogState.permission.category}
                  label="Category"
                  onChange={(e) => 
                    setDialogState({
                      ...dialogState,
                      permission: { ...dialogState.permission!, category: e.target.value },
                    })
                  }
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="h6" sx={{ mt: 2 }}>Plan Access</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan</TableCell>
                      <TableCell align="center">Access</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockPlans.map((plan) => (
                      <TableRow key={plan}>
                        <TableCell>{plan}</TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={dialogState.permission?.planAccess[plan] || false}
                            onChange={() => {
                              if (dialogState.permission) {
                                setDialogState({
                                  ...dialogState,
                                  permission: {
                                    ...dialogState.permission,
                                    planAccess: {
                                      ...dialogState.permission.planAccess,
                                      [plan]: !dialogState.permission.planAccess[plan],
                                    },
                                  },
                                });
                              }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSavePermission}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Permissions;